import React, {useState} from 'react';
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import pluralize, {singular} from 'pluralize';
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {invalidate, prefetch} from "services/query-client";
import {EyeIcon, LightBulbIcon, StarIcon as StarOutlineIcon} from "@heroicons/react/outline";
import {AnnotationIcon as AnnotationIconSolid, PencilIcon, PlusIcon, StarIcon, TrashIcon} from "@heroicons/react/solid";
import Button from "components/Button";
import {toTitleCase, truncate} from "services/string";
import Dropdown from "components/Dropdown";
import {Spotlight, SpotlightData} from "types/Spotlight";
import {useNavigate} from "react-router-dom";
import DeleteSpotlight from '../Delete';
import BooleanIndicator from "components/BooleanIndicator";
import {useNotifications} from "../../../context/NotificationContext";
import TernaryIndicator from "../../../components/TernaryIndicator";
import SpotlightStatus from "../../../types/SpotlightStatus";
import {useOperator} from "../../../context/OperatorContext";

// Types ----

type SpotlightUpdate = {
  id: number
  data: SpotlightData
}

// Helpers ----

function spotlightContentName(url: string, termFor: (term: string) => string) {
  const [scheme, path] = url.split(':');

  if (scheme.startsWith('mailto')) return `Email (${path})`;

  if (scheme.startsWith('tel')) return `Call (${path})`;

  if (scheme.startsWith('http')) return `URL (${path.substring(2)})`;

  if (scheme.startsWith('ngage')) {
    const parts = path.substring(2).split('/');

    if (parts.length > 1) {
      return `App Content (${toTitleCase(singular(termFor(parts[0])))})`
    }

    return `App Content (${toTitleCase(termFor(parts[0]))})`
  }

  return 'Unknown';
}

const statusMap: Record<SpotlightStatus, boolean | null> = {
  [SpotlightStatus.ENABLED]: true,
  [SpotlightStatus.PARTIAL]: null,
  [SpotlightStatus.DISABLED]: false,
}

// Component ----

export default function SpotlightList() {
  const navigate = useNavigate();
  const {notify} = useNotifications();
  const {termFor} = useOperator();

  const [deletingSpotlight, setDeletingSpotlight] = useState<Spotlight | null>(null);
  const [page, setPage] = useState<number>(1);

  const {data: spotlightPage, isLoading} = useQuery(
    ['spotlights.all', {page}],
    () => ngage.spotlights.filtered({page}),
    {keepPreviousData: true}
  )

  // Prefetch next and previous pages
  prefetch('spotlights.all', {page}, ngage.spotlights.filtered)

  const {mutateAsync: updateSpotlight} = useMutation(
    (update: SpotlightUpdate) => ngage.spotlights.update(update.id, update.data)
  )

  const enableSpotlight = async (spotlight: Spotlight) => {
    try {
      await updateSpotlight({
        id: spotlight.id,
        data: {is_enabled: true}
      })

      void invalidate('spotlights.all')

      notify("success", "Spotlight Enabled", `${spotlight.name} has been enabled`)
    } catch (error: any) {
      notify("error", "Something Went Wrong", `${spotlight.name} enabled status could not be changed`)
    }
  }

  const disableSpotlight = async (spotlight: Spotlight) => {
    try {
      await updateSpotlight({
        id: spotlight.id,
        data: {is_enabled: false}
      })

      void invalidate('spotlights.all')

      notify("success", "Spotlight Disabled", `${spotlight.name} has been disabled`)
    } catch (error: any) {
      notify("error", "Something Went Wrong", `${spotlight.name} enabled status could not be changed`)
    }
  }

  // Prepare options
  function getSpotlightOptions(spotlight: Spotlight) {

    const viewAction = {
      name: 'View',
      icon: EyeIcon,
      onClick: () => navigate(`/spotlights/${spotlight.id}`)
    };

    const editAction = {
      name: 'Edit',
      icon: PencilIcon,
      onClick: () => navigate(`/spotlights/${spotlight.id}/edit`)
    };

    const enableAction = {
      name: 'Enable',
      icon: StarIcon,
      onClick: () => enableSpotlight(spotlight)
    }

    const disableAction = {
      name: 'Disable',
      icon: StarOutlineIcon,
      onClick: () => disableSpotlight(spotlight)
    }

    const deleteAction = {
      name: 'Delete',
      icon: TrashIcon,
      onClick: () => setDeletingSpotlight(spotlight)
    }

    const actions = [viewAction, editAction, deleteAction]

    if (spotlight.status !== SpotlightStatus.DISABLED) {
      actions.splice(2, 0, disableAction)
    }

    if (spotlight.status !== SpotlightStatus.ENABLED) {
      actions.splice(2, 0, enableAction)
    }

    return actions
  }

  // Prepare breadcrumbs, metadata and actions
  const breadcrumbs = {'App Content': '/', 'Featured': '/', 'Spotlights': '/spotlights'}
  const meta = [{icon: AnnotationIconSolid, value: pluralize('spotlight', spotlightPage?.meta.total || 0, true)}]
  const actions = <Button icon={PlusIcon} onClick={() => navigate('/spotlights/new')}>New</Button>

  return (
    <Page
      title="Spotlights"
      subTitle={'Promote key content on the app overview screen'}
      breadcrumbs={breadcrumbs}
      meta={meta}
      actions={actions}
    >
      <Table paginationData={spotlightPage} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Title</TableHeader>
            <TableHeader>Link To</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Status</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Scheduled</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={isLoading}>

          {spotlightPage && spotlightPage.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={6}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <LightBulbIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No spotlights</span>
                </div>
              </TableData>
            </TableRow>
            : spotlightPage?.data.map((spotlight, index) => (
              <TableRow key={spotlight.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <Dropdown options={getSpotlightOptions(spotlight)} position='left'/>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <LightBulbIcon className="h-5 w-5 mr-2 text-gray-500"/>
                      {truncate(spotlight.name, 35)}
                    </div>
                  </div>
                </TableData>

                <TableData>
                  {truncate(spotlight.title, 35)}
                </TableData>

                <TableData>
                  {spotlight.url ? spotlightContentName(spotlight.url, termFor) : 'Unknown'}
                </TableData>

                <TableData alignment={TableData.alignments.CENTER}>
                  <TernaryIndicator value={statusMap[spotlight.status]}/>
                </TableData>

                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={spotlight.is_scheduled}/>
                </TableData>

                <TableData alignment={TableData.alignments.RIGHT}>
                  <Dropdown options={getSpotlightOptions(spotlight)}/>
                </TableData>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      {/* Delete confirmation modal */}
      <DeleteSpotlight
        spotlight={deletingSpotlight}
        show={!!deletingSpotlight}
        onCancel={() => setDeletingSpotlight(null)}
        onDelete={() => setDeletingSpotlight(null)}
        onDeleteFail={() => setDeletingSpotlight(null)}
      />
    </Page>
  );
}